import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { useGraphService } from '../GraphContext';
import Style from './NodeTypes.module.css';

/**
 * Universal Node component that adapts based on node type
 * Types are inferred from node data:
 * 1. Source/Destination nodes - Top level system nodes (no mapping allowed)
 * 2. Parent nodes - Have children but can also be children themselves
 * 3. Leaf nodes - No children, only these can have mapping edges
 */
export const UniversalNode = memo(({ id, data, isConnectable }) => {
  const { handleNodeCollapse, nodes, labels } = useGraphService();
 
  // Helper function to lookup a node by id from the context's node list.
  const getNodeById = useCallback(
    (nodeId) => nodes.find(n => n.id === nodeId),
    [nodes]
  );
 
  /**
   * Recursively determines whether the node (or any of its ancestors)
   * qualifies as a source node.
   *
   * @param {string} nodeId - ID of the node to check.
   * @returns {boolean} - True if this node or any parent node is a source.
   */
  const checkIsSource = useCallback(
    (nodeId) => {
      // A node qualifies as source if its id includes 'source' or is 'customer'
      if (nodeId.includes('source') || nodeId === 'customer') {
        return true;
      }
      // Look up the node and check if it has a parent
      const node = getNodeById(nodeId);
      if (node && node.data && node.data.parentId) {
        return checkIsSource(node.data.parentId);
      }
      return false;
    },
    [getNodeById]
  );
 
 
  // Determine if this is a source (left side) or target (right side) node
  // We infer this from the node ID or parent hierarchy
  const isSource = checkIsSource(id);

  // Determine node level/type based on data
  const isRootNode = data.isRootNode;
  const isParentNode = data.isParent || data.hasChildren;
  const isLeafNode = !isRootNode && !isParentNode;
 
  const colorScheme = {
    border: data.selected ? Style.selected : '',
    handle: '#1e54b8'
  };

  // Handle node collapse
  const handleCollapse = useCallback((event) => {
    // Stop event propagation to prevent node selection
    event.stopPropagation();
    handleNodeCollapse(id);
  }, [id, handleNodeCollapse]);

  // Set node appearance based on type
  const styles = {
    rootNode: `${Style.node} ${Style.rootNode} ${colorScheme.border}`,
    parentNode: `${Style.node} ${Style.parentNode} ${colorScheme.border} ${data.isCollapsed ? Style.collapsed : ''}`,
    leafNode: `${Style.node} ${Style.leafNode} ${colorScheme.border}`
  };
 
  // Determine handle visibility and positions
  const handles = [];
 
  if (isRootNode) {
    // Different handle positions for customer and client root nodes
    if (id === 'customer') {
      // Customer root node has right handle
      handles.push({ type: 'source', position: Position.Right, id: 'b' });
    } else if (id === 'client') {
      // Client root node has left handle
      handles.push({ type: 'source', position: Position.Left, id: 'b' });
    } else {
      // Default for other root nodes
      handles.push({ type: 'source', position: Position.Right, id: 'b' });
    }
  } else if (isParentNode) {
    // Parent nodes have top, bottom, and either left or right based on source/destination
    handles.push({ type: 'target', position: Position.Top, id: 't' });
    handles.push({ type: 'source', position: Position.Bottom, id: 'b' });
   
    if (isSource) {
      handles.push({ type: 'source', position: Position.Right, id: 'r' });
    } else {
      handles.push({ type: 'target', position: Position.Left, id: 'l' });
    }
  } else if (isLeafNode) {
    // Leaf nodes have different target handle positions based on parent
    if (data && data.parentId) {
      if (data.parentId === 'customer' || (checkIsSource(data.parentId) && data.parentId !== 'client')) {
        // Customer children have left target handle
        handles.push({ type: 'target', position: Position.Left, id: 't' });
      } else if (data.parentId === 'client' || !checkIsSource(data.parentId)) {
        // Client children have right target handle
        handles.push({ type: 'target', position: Position.Right, id: 't' });
      } else {
        // Default fallback
        handles.push({ type: 'target', position: Position.Top, id: 't' });
      }
    } else {
      // Default for leaf nodes without parent
      handles.push({ type: 'target', position: Position.Top, id: 't' });
    }
   
    if (isSource) {
      handles.push({ type: 'source', position: Position.Right, id: 'r' });
    } else {
      handles.push({ type: 'target', position: Position.Left, id: 'l' });
    }
  }
 
 
 
  return (
    <div className="position-relative">
      {/* Render the appropriate node type */}
      {isRootNode && (
        <div className={styles.rootNode}>
          {id === 'customer' ? labels?.customerLabel || data.label : id === 'client' ? labels?.clientLabel || data.label : data.label}
        </div>
      )}

      {isParentNode && !isRootNode && (
        <div className={styles.parentNode}>
          <div className="mb-1">{data.label}</div>
          <button
            type="button"
            onClick={handleCollapse}
            className={`mt-2 ${Style.collapseButton}`}
          >
            {data.isCollapsed ? 'Expand' : 'Collapse'}
          </button>
        </div>
      )}

      {isLeafNode && (
        <div className={styles.leafNode}>
          <div className={Style.leafContent}>
            <span>{data.label}</span>
            {(data?.fieldType || data?.dataType || data?.type || data?.data_type || data?.field_type) && (
              <span className={Style.fieldType}>
                ({(data.fieldType || data.dataType || data.type || data.data_type || data.field_type)})
              </span>
            )}
          </div>
        </div>
      )}
     
      {/* Render handles */}
      {handles.map(handle => (
        <Handle
          key={handle.id}
          type={handle.type}
          position={handle.position}
          id={handle.id}
          style={{ background: colorScheme.handle }}
          isConnectable={isConnectable && isLeafNode} // Only leaf nodes are connectable
        />
      ))}
    </div>
  );
});
 
// Export this as the default node type
export default UniversalNode;